import { connect } from "react-redux";
import { UncontrolledTooltip } from "reactstrap";
import Octicon, { IssueOpened } from "@githubprimer/octicons-react";
import { Cell, Column, ColumnCellProps, Table } from "fixed-data-table-2";
import * as React from "react";
import dimensions from "react-dimensions";
import { IGlobalState } from "../reducers/IndexReducer";
import ICsvData from "../models/ICsvData";
import CsvValidation from "../models/CsvValidation";
import TooltipErrorBoundary from "./TooltipErrorBoundary";

const inbreedingCoefficientThreshold = 6.25;

interface IProps {
  csvData: ICsvData[];
  isValid: CsvValidation;
  containerWidth: number;
  inbreedingCalcs: any[];
}

const mapStateToProps = (state: IGlobalState) => ({
  csvData: state.csvUploaderState.csvData,
  isValid: state.csvUploaderState.isValid,
  inbreedingCalcs: state.inbreedingState.calculatedTableRows,
});

const IMPOSSIBLE_STATE_ERROR =
  "This state should not be reachable. If this error occurs, this is a bug!";

class InbreedingCalculatorResultsGridWithoutDimensions extends React.PureComponent<
  IProps
> {
  // TODO: add action to remove column from store - but still works if delete bull from the selector

  public staticColumnRenderer = (
    title: string,
    name: string,
    classname: string,
    width: number,
    fixed: boolean
  ) => (
    <Column
      header={<Cell className={classname}>{title}</Cell>}
      columnKey={name}
      cell={this.parentageCellRenderer}
      width={width}
      fixed={fixed}
    />
  );

  public cowIdCellRenderer = ({
    rowIndex,
    columnKey,
    ...props
  }: ColumnCellProps) => {
    const { inbreedingCalcs } = this.props;
    if (columnKey === undefined) {
      throw new Error(IMPOSSIBLE_STATE_ERROR);
    }
    return (
      <Cell className="cowTableCell" {...props}>
        {inbreedingCalcs[rowIndex][columnKey]}
      </Cell>
    );
  };

  public parentageCellRenderer = ({
    rowIndex,
    columnKey,
    ...props
  }: ColumnCellProps) => {
    const { inbreedingCalcs } = this.props;
    if (columnKey === undefined) {
      throw new Error(IMPOSSIBLE_STATE_ERROR);
    }
    const sire = inbreedingCalcs[rowIndex][columnKey];

    if (sire === "" || sire == null) {
      const keyIndex = columnKey.replace(/\s/g, "").replace(/\//g, "");
      const tooltipIndex = `tooltip-${rowIndex}-${keyIndex}`;
      let errorMsg;

      if (columnKey === "Cow number/ name") {
        errorMsg = "Cow missing";
      } else if (columnKey === "Cow Sire") {
        errorMsg = "Cow Sire missing";
      } else if (columnKey === "Maternal Grandsire") {
        errorMsg = "Cow Maternal Grandsire missing";
      } else {
        errorMsg = "Unknown error";
      }
      return (
        <div>
          <Cell className="sireTableCell" {...props} id={tooltipIndex}>
            <div className="infoIcon">
              <Octicon icon={IssueOpened} size="small" />
            </div>
          </Cell>
          <TooltipErrorBoundary>
            <UncontrolledTooltip
              placement="bottom"
              target={tooltipIndex}
              className="tooltip"
            >
              {errorMsg}
            </UncontrolledTooltip>
          </TooltipErrorBoundary>
        </div>
      );
    }

    return (
      <Cell className="sireTableCell" {...props}>
        {sire}
      </Cell>
    );
  };

  public coefficientCellRenderer = ({
    rowIndex,
    columnKey,
    ...props
  }: ColumnCellProps) => {
    const { inbreedingCalcs } = this.props;
    if (columnKey === undefined) {
      throw new Error(IMPOSSIBLE_STATE_ERROR);
    }
    const val = inbreedingCalcs[rowIndex][columnKey];

    let valueToShow = "";
    let className = "inbreedingCell";

    if (val !== "" && val != null) {
      valueToShow = (Math.round(val * 100) / 100).toFixed(2);
      if (val > inbreedingCoefficientThreshold) {
        className = "inbreedingCellOverThreshold";
      }
    }

    return (
      <Cell className={className} {...props}>
        {valueToShow}
      </Cell>
    );
  };

  public render() {
    const { containerWidth, isValid, inbreedingCalcs } = this.props;

    // not render bull code selector if invalid csv file
    if (isValid !== CsvValidation.Valid) {
      return null;
    }

    const regexp = /^[0-9]+/;

    return (
      <div>
        <Table
          className="tableStyle"
          rowsCount={inbreedingCalcs.length}
          rowHeight={40}
          headerHeight={40}
          width={containerWidth}
          height={500}
          {...this.props}
        >
          {/* static columns */}
          {this.staticColumnRenderer(
            "Cow number/ name",
            "Cow number/ name",
            "cowTableHeader",
            100,
            true
          )}
          {this.staticColumnRenderer(
            "Cow Sire",
            "Cow Sire",
            "sireTableHeader",
            80,
            true
          )}
          {this.staticColumnRenderer(
            "Maternal Grandsire",
            "Maternal Grandsire",
            "sireTableHeader",
            80,
            true
          )}

          {/* bull columns */}
          {Object.keys(inbreedingCalcs[0])
            .map((bullCode) => {
              if (!regexp.test(bullCode)) {
                return undefined;
              }
              return (
                <Column
                  header={
                    <Cell className="inbreedingTableHeader">{bullCode}</Cell>
                  }
                  columnKey={bullCode}
                  cell={this.coefficientCellRenderer}
                  width={80}
                  key={bullCode}
                />
              );
            })
            .filter((value) => !!value)}
        </Table>
      </div>
    );
  }
}

const InbreedingCalculatorResultsGridWithDimensions = dimensions()(
  InbreedingCalculatorResultsGridWithoutDimensions
);

function InbreedingCalculatorResultsGrid(props: any) {
  return (
    // force dimesions to be based on div container, not the browser window
    <div className="container inbreedingTable">
      <InbreedingCalculatorResultsGridWithDimensions {...props} />
    </div>
  );
}

export default connect<any, any, any>(mapStateToProps)(
  InbreedingCalculatorResultsGrid
);



// WEBPACK FOOTER //
// ./src/scripts/components/Table.tsx